import { useFeatureFlags, useTenantConfig } from 'contexts';
import { useAuth } from '@teko/auth-react';
import {
  AddToCartProps,
  ProductCardProps,
  ProductCardCategory,
} from '@teko/types/ecommerce';
import { useCallback } from 'react';
import { ProductImage } from 'types';
import {
  isAdultCategoryProduct,
  isServerless,
  requiredConfirm18YearsOld,
} from 'helpers';
import Over18YearOldImg from 'assets/18+.svg';
import sgcOver18YearOldImg from 'assets/sgc_img_18+.svg';

interface ProductConverted {
  images: ProductImage[];
  imageUrl: string;
  actionReady: boolean;
  addToCart?: AddToCartProps;
}

interface Product {
  categories?: ProductCardCategory[];
  images?: ProductImage[];
  imageUrl?: string;
  addToCart?: AddToCartProps;
}

interface UseProduct {
  convertProduct: (product: Product) => ProductConverted;
  transformProductWhenUserUnder18: (
    product: ProductCardProps,
  ) => ProductCardProps;
}

interface UseHandlerUnder18 {
  handleUnder18?: () => void;
}

export const useHandlerUnderEighteenYearOld = (
  props: UseHandlerUnder18 = {},
): UseProduct => {
  const { isBot, tenant } = useTenantConfig();
  const { flags } = useFeatureFlags();
  const { user, loading: authLoading } = useAuth();
  const { handleUnder18 } = props;

  const isSGC = tenant === 'sgc';

  // EPIC-315: effect replace images when user is under 18 years old
  const convertProduct = useCallback(
    (product: Product): ProductConverted => {
      const { addToCart } = product;
      const emptyImages =
        product?.images?.map((image) => ({
          ...image,
          src: '',
        })) || [];
      // if bot or product doesn't has adult category, no need to check age
      if (
        !flags.epic_ecom_315 ||
        isBot ||
        !isAdultCategoryProduct(product.categories)
      )
        return {
          images: product?.images || [],
          imageUrl: product?.imageUrl || '',
          actionReady: true,
          addToCart,
        };
      // if isn't serverless, no need to check age
      if (!isServerless() || authLoading) {
        return {
          images: emptyImages,
          imageUrl: '',
          actionReady: false,
        };
      } else {
        const requireCheckAge = requiredConfirm18YearsOld(user);

        if (!requireCheckAge) {
          return {
            images: product?.images || [],
            imageUrl: product?.imageUrl || '',
            actionReady: true,
            addToCart,
          };
        } else {
          handleUnder18?.();
          return {
            images:
              product?.images?.map((image) => ({
                ...image,
                src: isSGC ? sgcOver18YearOldImg : Over18YearOldImg,
              })) || [],
            imageUrl: isSGC ? sgcOver18YearOldImg : Over18YearOldImg,
            actionReady: false,
          };
        }
      }
    },
    [user, authLoading, isBot],
  );

  const transformProductWhenUserUnder18 = useCallback(
    (product: ProductCardProps): ProductCardProps => {
      // if bot or product doesn't has adult category, no need to check age
      if (!flags.epic_ecom_315 || isBot || !product.isAlcoholicCategory)
        return {
          ...product,
        };
      // if isn't serverless, no need to check age
      if (!isServerless() || authLoading) {
        return {
          ...product,
          imageUrl: '',
          // Hidden all button in this case
          addToCart: undefined,
          ctaButton: undefined,
          contactButton: undefined,
        };
      } else {
        const requireCheckAge = requiredConfirm18YearsOld(user);

        if (!requireCheckAge) {
          return {
            ...product,
          };
        } else {
          handleUnder18?.();
          return {
            ...product,
            imageUrl: isSGC ? sgcOver18YearOldImg : Over18YearOldImg,
            // Hidden all button in this case
            addToCart: undefined,
            ctaButton: undefined,
            contactButton: undefined,
          };
        }
      }
    },
    [user, authLoading, isBot],
  );

  return {
    convertProduct,
    transformProductWhenUserUnder18,
  };
};
